/**
 * NUCLEAR SLUG SCRUBBER
 * 
 * Aggressively removes technical slugs from product data across all recommendation cards.
 * This is the single source of truth for slug removal - apply at multiple levels:
 * 1. Data processing (PicksTab.tsx)
 * 2. Component entry (all card components)
 * 3. Render time (final check before display)
 * 
 * EXACT TRANSFORM: .replace(/-[a-z0-9-]+$/, '').replace(/-/g, ' ')
 * This ensures 'Toys-chew-to' becomes 'Toys' and 'beds_furniture' becomes 'beds furniture'
 */

/**
 * Nuclear slug scrubbing - removes ALL technical slug patterns
 * @param str - String to scrub (title, name, category, subcategory, etc.)
 * @returns Scrubbed string with all technical slugs removed
 */
export function nuclearScrubSlug(str: string | undefined | null): string {
  if (!str || typeof str !== 'string') return ''
  
  const original = str.trim()
  
  // NUCLEAR: First check if entire string is a slug - if so, return empty immediately
  if (/^(toys|walk|health|food|beds)-[a-z0-9-]+$/i.test(original)) {
    return ''
  }
  
  // NUCLEAR: Check for specific known slug patterns FIRST and return empty immediately
  // CRITICAL: Include truncated versions (CSS line-clamp can truncate slugs)
  const knownSlugPatterns = [
    /toys-chew-to/i,
    /toys-fetch-t/i,
    /toys-puzzle-/i,
    /toys-rope-/i,
    /toys-treat-/i,
    /toys-variety/i,
    /toys-interac/i,
    /toys-all-toy/i,
    /toys-tennis-/i,
    /walk-essenti/i,
    /walk essenti/i,  // Truncated version (space instead of hyphen)
    /health-welln/i,
    /health welln/i,  // Truncated version
    /food-nutriti/i,
    /food nutriti/i,  // Truncated version
    /beds-furnitu/i,
    /beds furnitu/i,  // Truncated version
  ]
  
  // Check original string first (including truncated versions)
  if (knownSlugPatterns.some(pattern => pattern.test(original))) {
    return ''
  }
  
  // CRITICAL: Also check for truncated slug patterns (e.g., "Walk Essenti" from "Walk-essenti")
  // These can appear when CSS truncates hyphenated slugs
  const truncatedSlugPatterns = [
    /^walk essenti/i,
    /^health welln/i,
    /^food nutriti/i,
    /^beds furnitu/i,
    /^toys chew/i,
    /^toys fetch/i,
    /^toys puzzle/i,
    /^toys rope/i,
    /^toys treat/i,
  ]
  
  if (truncatedSlugPatterns.some(pattern => pattern.test(original))) {
    return ''
  }
  
  // NUCLEAR REGEX SCRUBBING: Apply exact transform as specified
  // .replace(/-[a-z0-9-]+$/, '').replace(/-/g, ' ')
  // This ensures 'Toys-chew-to' becomes 'Toys' and 'beds_furniture' becomes 'beds furniture'
  let scrubbed = original.replace(/-[a-z0-9-]+$/, '').replace(/-/g, ' ').trim()
  
  // NUCLEAR: Additional aggressive patterns to catch edge cases
  // Remove any remaining hyphenated technical patterns
  scrubbed = scrubbed.replace(/(toys|walk|health|food|beds)-[a-z0-9-]+/gi, '').trim()
  
  // Remove if entire string is a slug pattern (check again after first transform)
  if (/^(toys|walk|health|food|beds)-[a-z0-9-]+$/i.test(scrubbed)) {
    return ''
  }
  
  // Check scrubbed result against known patterns
  if (knownSlugPatterns.some(pattern => pattern.test(scrubbed))) {
    return ''
  }
  
  // Final check: if it still contains hyphenated pattern, return empty
  if (/-[a-z0-9-]+/i.test(scrubbed)) {
    // Check if it matches slug pattern - if so, return empty
    if (/(toys|walk|health|food|beds)-[a-z0-9-]+/i.test(scrubbed)) {
      return ''
    }
    // NUCLEAR: If it contains any hyphenated pattern that looks like a slug, return empty
    // Don't try to extract first word - just return empty to be safe
    return ''
  }
  
  return scrubbed
}

/**
 * Check if a string is a technical slug pattern
 * @param str - String to check
 * @returns true if string matches slug patterns
 */
export function isSlugPattern(str: string | undefined | null): boolean {
  if (!str || typeof str !== 'string') return false
  
  const lower = str.toLowerCase().trim()
  
  // CRITICAL: Only match ACTUAL technical slug patterns, not valid category names
  // Technical slugs have specific patterns: "toys-chew-to", "walk-essenti", etc.
  // Valid category names like "Tie-Outs", "ID Tags" should NOT be flagged
  
  // Check for exact technical slug patterns (bundle prefix + hyphenated suffix)
  const technicalSlugPatterns = [
    /^(toys|walk|health|food|beds)-[a-z0-9-]+$/i,  // Exact technical slug: "toys-chew-to", "walk-essenti"
  ]
  
  // Check if matches exact technical slug pattern
  if (technicalSlugPatterns.some(pattern => pattern.test(lower))) {
    return true
  }
  
  // Check for specific known technical slug patterns
  const knownSlugPatterns = [
    /toys-chew-to/i,
    /toys-fetch-t/i,
    /toys-puzzle-/i,
    /toys-rope-/i,
    /toys-treat-/i,
    /toys-variety/i,
    /toys-interac/i,
    /toys-all-toy/i,
    /toys-tennis-/i,
    /walk-essenti/i,
    /health-welln/i,
    /food-nutriti/i,
    /beds-furnitu/i,
  ]
  
  if (knownSlugPatterns.some(pattern => pattern.test(lower))) {
    return true
  }
  
  // Check for technical slug keywords (bundle prefix + hyphen or space)
  // CRITICAL: Also check for truncated versions (e.g., "Walk Essenti" from "Walk-essenti")
  const slugKeywords = ['toys-', 'walk-', 'health-', 'food-', 'beds-', 'toys ', 'walk essenti', 'health welln', 'food nutriti', 'beds furnitu']
  if (slugKeywords.some(keyword => lower.startsWith(keyword) || lower.includes(keyword))) {
    return true
  }
  
  // DO NOT flag valid category names with hyphens (e.g., "Tie-Outs", "ID Tags")
  // Only flag if it starts with a bundle prefix followed by hyphenated pattern
  return false
}

/**
 * Nuclear scrub for product object - scrubs all text fields
 * @param product - Product object with title, name, category, subcategory
 * @returns Product object with all text fields scrubbed
 */
export function nuclearScrubProduct(product: {
  title?: string
  name?: string
  category?: string
  subcategory?: string
  [key: string]: any
}): typeof product {
  return {
    ...product,
    title: nuclearScrubSlug(product.title),
    name: nuclearScrubSlug(product.name),
    category: nuclearScrubSlug(product.category),
    subcategory: nuclearScrubSlug(product.subcategory),
  }
}

/**
 * Bundle names that should NEVER appear on product cards
 */
export const BUNDLE_NAMES_TO_FILTER = [
  // Full bundle names
  'Walk Essentials',
  'Health & Wellness',
  'Toys',
  'Food & Nutrition',
  'Beds & Furniture',
  // Truncated variations (CSS line-clamp can truncate)
  'Walk Essenti',
  'Health Welln',
  'Health Wellness',
  'Health and Wellness',
  'Food Nutriti',
  'Beds Furnitu',
  // Lowercase variations
  'walk essentials',
  'health & wellness',
  'toys',
  'food & nutrition',
  'beds & furniture',
  // Hyphenated subcategory slugs (from CSV data) - these should NEVER appear
  'Toys-chew-to',
  'Toys-fetch-t',
  'Toys-puzzle-',
  'Toys-rope-&-',
  'Toys-treat-d',
  'Toys-variety',
  'Toys-interac',
  'Toys-all-toy',
  'Toys-tennis-',
  'toys-chew-to',
  'toys-fetch-t',
  'toys-puzzle-',
  'toys-rope-&-',
  'toys-treat-d',
  'toys-variety',
  'toys-interac',
  'Walk-essenti',
  'Health-welln',
  'Food-nutriti',
  'Beds-furnitu',
]

/**
 * Check if a string is a bundle name that should be filtered
 */
export function isBundleName(str: string | undefined | null): boolean {
  if (!str || typeof str !== 'string') return false
  
  const lower = str.toLowerCase().trim()
  return BUNDLE_NAMES_TO_FILTER.some(bundleName => {
    if (!bundleName) return false
    const bundleLower = String(bundleName).toLowerCase().trim()
    return lower === bundleLower || lower.includes(bundleLower)
  })
}

